/**
 * NZOS Control Service Adapter
 * This adapter provides the same interface as the Control service but uses NZOS API endpoints.
 * It allows seamless replacement of Control service calls with NZOS service calls.
 *
 * Note: This is a temporary adapter using mock data. Type assertions are used liberally
 * since the NZOS API structure is not finalized. This will be refined when the real API is available.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { logger } from '@accedo/logger';
import localConfig from '##/services/elevate/providers/config/control/localConfig.json';
import { apiCall } from './adapter';
// Import localConfig.json for development overrides

import type {
  Options,
  EntryOptions,
  PaginationEntryOptions,
  Pagination,
  Entry,
  Meta,
  Assets,
  ApplicationStatus,
  LocaleInfo,
} from './types';

// Logger prefix
const LP = '[NZOSControlAdapter]';

// Import and re-export types from local types file for compatibility
export type {
  Options,
  EntryOptions,
  PaginationEntryOptions,
  Pagination,
  Entry,
  Meta,
  Assets,
  ApplicationStatus,
  LocaleInfo,
  MetaData, // Re-exported for compatibility
} from './types';

// Session management - for NZOS we'll use a simple session token approach
let sessionToken: string | null = null;

// Cache for NZOS config to avoid multiple calls to the same endpoint
let configCache: Record<string, unknown> | null = null;
let configCachePromise: Promise<Record<string, unknown>> | null = null;

/**
 * Get NZOS configuration with caching.
 * This ensures the config endpoint is only called once during app initialization.
 * Merges API response with localConfig.json for development overrides.
 */
async function getCachedConfig(): Promise<Record<string, unknown>> {
  // If we already have cached data, return it
  if (configCache) {
    logger.debug(`${LP} Using cached config`);
    return configCache;
  }

  // If a request is in progress, wait for it
  if (configCachePromise) {
    logger.debug(`${LP} Waiting for in-flight config request`);
    return configCachePromise;
  }

  // Make a new request and cache the promise
  logger.info(`${LP} Fetching config from NZOS API`);
  configCachePromise = apiCall<Record<string, unknown>>('/config')
    .then((apiConfig) => {
      // Deep merge localConfig with API config
      // API config takes precedence except for local-only settings like application.player
      const apiApplication = apiConfig.application as any;
      const localApplication = localConfig.application as any;

      const merged = {
        ...apiConfig,
        application: {
          ...(apiApplication || {}),
          ...(localApplication || {}),
          // Ensure nested properties are also merged
          i18n: {
            ...(apiApplication?.i18n || {}),
            ...(localApplication?.i18n || {}),
          },
          navigation: {
            ...(apiApplication?.navigation || {}),
            ...(localApplication?.navigation || {}),
          },
          // Local-only settings that override API
          player: localApplication?.player,
          search: localApplication?.search || apiApplication?.search,
          theme: localApplication?.theme || apiApplication?.theme,
        },
      };

      logger.debug(`${LP} Merged config with localConfig overrides:`, merged);
      configCache = merged;
      configCachePromise = null;
      return merged;
    })
    .catch((error) => {
      configCachePromise = null;
      throw error;
    });

  return configCachePromise;
}

/**
 * Get or create a session with NZOS API.
 * This replaces Control's getSession function.
 */
export async function getSession(_options?: Options): Promise<string | null> {
  try {
    // For now, we'll use a simple session approach
    // In real implementation, this would call NZOS auth endpoints
    if (!sessionToken) {
      logger.info(`${LP} Creating new NZOS session`);
      // Call NZOS config endpoint to establish connection
      const config = await getCachedConfig();
      sessionToken = (config.sessionId as string) || `nzos-session-${Date.now()}`;
    }
    return sessionToken;
  } catch (error) {
    logger.error(`${LP} Failed to get session:`, error);
    return null;
  }
}

/**
 * Get application status from NZOS.
 * This replaces Control's getApplicationStatus function.
 */
export async function getApplicationStatus(_options?: Options): Promise<ApplicationStatus> {
  try {
    logger.info(`${LP} Getting application status`);
    const config = await getCachedConfig();
    return {
      status: (config.status as string) || 'active',
      message: 'Application is running',
    };
  } catch (error) {
    logger.error(`${LP} Failed to get application status:`, error);
    return {
      status: 'error',
      message: 'Failed to get application status',
    };
  }
}

/**
 * Get all metadata from NZOS configuration.
 * This replaces Control's getAllMetadata function.
 */
export async function getAllMetadata(_options?: Options): Promise<Meta> {
  try {
    logger.info(`${LP} Getting all metadata`);
    logger.debug(`${LP} getAllMetadata called`);
    const config = await getCachedConfig();
    logger.debug(`${LP} Config received:`, config);

    // Transform NZOS config to match Control metadata structure
    // The ControlConfigService expects metadata with: featureFlags, providers, application
    // Merge API response with local config to preserve player settings from localConfig.json
    const configApplication = config.application as any;
    const metadata: Meta = {
      featureFlags: config.featureFlags || {
        appIdGidEasterEggActive: false,
        debugOverlayVisible: false,
      },
      providers: config.providers || {
        cms: {
          accedoControl: {
            routesToPagesMappingList: 'routes-mapping-list-id',
            menu: 'menu-id',
          },
        },
        ovp: {
          serviceCore: {
            url: 'http://localhost:3001',
          },
        },
        i18n: {
          accedoControl: {
            dictionary: 'dictionary-id',
          },
        },
      },
      application: {
        name: configApplication?.name || 'NZOS CTV',
        navigation: configApplication?.navigation || {
          defaultRoute: '/',
        },
        i18n: configApplication?.i18n || {
          defaultLocale: 'en-NZ',
        },
        // Preserve player config from localConfig.json (not in API response)
        player: configApplication?.player,
        search: configApplication?.search,
        theme: configApplication?.theme,
      },
    };

    return metadata;
  } catch (error) {
    logger.error(`${LP} Failed to get metadata:`, error);
    // Return fallback metadata structure
    return {
      featureFlags: {
        appIdGidEasterEggActive: false,
        debugOverlayVisible: false,
      },
      providers: {
        cms: {
          accedoControl: {
            routesToPagesMappingList: 'routes-mapping-list-id',
            menu: 'menu-id',
          },
        },
        ovp: {
          serviceCore: {
            url: 'http://localhost:3001',
          },
        },
        i18n: {
          accedoControl: {
            dictionary: 'dictionary-id',
          },
        },
      },
      application: {
        name: 'NZOS CTV',
        navigation: {
          defaultRoute: '/',
        },
        i18n: {
          defaultLocale: 'en-NZ',
        },
      },
    };
  }
}

/**
 * Get all assets from NZOS configuration.
 * This replaces Control's getAllAssets function.
 * Note: NZOS API returns the full config directly, not nested under 'assets'.
 */
export async function getAllAssets(_options?: Options): Promise<Assets> {
  try {
    logger.info(`${LP} Getting all assets (full NZOS config)`);
    const config = await getCachedConfig();
    // NZOS API returns the full config, not config.assets
    return config as Assets;
  } catch (error) {
    logger.error(`${LP} Failed to get assets:`, error);
    return {};
  }
}

/**
 * Get entry by ID from NZOS CMS.
 * This replaces Control's getEntryById function.
 */
export async function getEntryById(id: string, _options?: EntryOptions): Promise<Entry> {
  try {
    logger.info(`${LP} Getting entry by ID: ${id}`);
    // For now, we'll try to get from CMS routes
    const routes = await apiCall<{ routes?: any[] }>('/cms/routes');

    const route = routes.routes?.find((item: any) => item?.id === id);

    if (route) {
      return route as Entry;
    }

    // If not found in routes, return a minimal Entry with _meta
    logger.warn(`${LP} Entry not found: ${id}, returning minimal entry`);
    return {
      _meta: {
        id,
        typeId: '',
        typeAlias: '',
        publishedFrom: new Date().toISOString(),
      },
    } as Entry;
  } catch (error) {
    logger.error(`${LP} Failed to get entry by ID:`, error);
    throw error;
  }
}

/**
 * Get multiple entries by IDs from NZOS CMS.
 * This replaces Control's getEntriesByIds function.
 */
export async function getEntriesByIds(
  ids: string[],
  _options?: PaginationEntryOptions,
): Promise<{ entries: Entry[]; pagination: Pagination }> {
  try {
    logger.info(`${LP} Getting entries by IDs:`, ids);

    // Get all routes
    const routes = await apiCall<{ routes?: any[] }>('/cms/routes');

    // Filter entries by IDs
    const entries = (routes.routes?.filter((item: any) => ids.includes(item.id)) || []) as Entry[];

    return {
      entries,
      pagination: {
        offset: 0,
        size: entries.length,
        total: entries.length,
      },
    };
  } catch (error) {
    logger.error(`${LP} Failed to get entries by IDs:`, error);
    return {
      entries: [],
      pagination: { offset: 0, size: 0, total: 0 },
    };
  }
}

/**
 * Get entries by aliases from NZOS CMS.
 * This replaces Control's getEntriesByAliases function.
 */
export async function getEntriesByAliases(
  aliases: string[],
  _options?: PaginationEntryOptions,
): Promise<{ entries: Entry[]; pagination: Pagination }> {
  try {
    logger.info(`${LP} Getting entries by aliases:`, aliases);

    // Get all routes
    const routes = await apiCall<{ routes?: any[] }>('/cms/routes');

    // Filter entries by aliases
    const entries = (routes.routes?.filter((item: any) => item.alias && aliases.includes(item.alias)) || []) as Entry[];

    return {
      entries,
      pagination: {
        offset: 0,
        size: entries.length,
        total: entries.length,
      },
    };
  } catch (error) {
    logger.error(`${LP} Failed to get entries by aliases:`, error);
    return {
      entries: [],
      pagination: { offset: 0, size: 0, total: 0 },
    };
  }
}

/**
 * Get available locales from NZOS.
 * This replaces Control's getLocales function.
 */
export async function getLocales(_options?: Options): Promise<LocaleInfo[]> {
  try {
    logger.info(`${LP} Getting locales`);

    // Get config which should contain locale information
    const config = await apiCall<{ locales?: LocaleInfo[] }>('/config');

    return (
      config.locales || [
        { code: 'en-NZ', displayName: 'English (New Zealand)' },
        { code: 'en-US', displayName: 'English (US)' },
      ]
    );
  } catch (error) {
    logger.error(`${LP} Failed to get locales:`, error);
    return [{ code: 'en-NZ', displayName: 'English (New Zealand)' }];
  }
}
